import { Separator, Stack } from '@fluentui/react';
import {
  IKeyValuePairEditorClassnames,
  IKeyValuePairEditorStyles,
  keyValuePairEditorStyles,
  Loader,
  LoaderType,
  useClassNames,
} from '@h2oai/ui-kit';
import { useCallback, useMemo } from 'react';

import { RoleBinding } from '../../../../authz/gen/ai/h2o/authorization/v1/role_binding_pb';
import { OwnerRoleKey } from '../../../../authz/providers/RoleProvider';
import { useUsers } from '../../../../authz/providers/UsersProvider';
import { MemberListItem } from './MemberListItem';
import { membersListStyles } from './styles';
import { ManageMembersProps } from './types';

const isOwnerRole = (role?: string) => {
  return role?.includes(OwnerRoleKey.WORKSPACE_OWNER) || role?.includes(OwnerRoleKey.PERSONAL_WORKSPACE_OWNER);
};

export const ManageMembers = (props: ManageMembersProps) => {
  const { prepopulatedRoleBindings, readOnly = false, loading = false } = props;
  const { usersAndGroupsNameMap } = useUsers();
  const usersWithRoles = useMemo(() => {
    if (!prepopulatedRoleBindings || !usersAndGroupsNameMap) return [];

    const uniqueUsers = new Set(
      prepopulatedRoleBindings.map((item) => item.subject).filter((subject): subject is string => Boolean(subject))
    );

    return [...uniqueUsers].map((userName) => usersAndGroupsNameMap[userName] ?? { name: userName });
  }, [prepopulatedRoleBindings, usersAndGroupsNameMap]);
  const hasMoreThanOneOwner = useMemo(() => {
    // Get unique subjects that have owner roles
    const uniqueOwnerSubjects = new Set(
      prepopulatedRoleBindings
        ?.filter((binding) => isOwnerRole(binding?.role))
        .map((binding) => binding.subject)
        .filter(Boolean)
    );
    return uniqueOwnerSubjects.size > 1;
  }, [prepopulatedRoleBindings]);

  const usersInWorkspaceRolesList = useMemo(() => usersWithRoles.map(({ name }) => name || ''), [usersWithRoles]);

  const getUserRoleBindings = useCallback(
    (userName?: string): RoleBinding[] => {
      if (!prepopulatedRoleBindings || !usersAndGroupsNameMap || !userName) return [];

      return prepopulatedRoleBindings?.filter((roleBinding) => roleBinding.subject === userName);
    },
    [usersAndGroupsNameMap, prepopulatedRoleBindings]
  );
  const handleUpdateRoleBindings = useCallback(
    (roleBinding: RoleBinding[], isCreate: boolean) => {
      props.onMembersChange(roleBinding, isCreate);
    },
    [usersAndGroupsNameMap, prepopulatedRoleBindings]
  );

  const classNames = useClassNames<IKeyValuePairEditorStyles, IKeyValuePairEditorClassnames>(
    'MembersList',
    keyValuePairEditorStyles,
    membersListStyles
  );

  return (
    <Stack className={classNames.root}>
      <Stack horizontal horizontalAlign={'space-between'} className={classNames.header}>
        <div className={classNames.keyColumn}>
          <span>Member Name</span>
        </div>
        <div className={classNames.valueColumn}>
          <span>Permission</span>
        </div>
        <div className={classNames.buttonColumn}>
          <span>Actions</span>
        </div>
      </Stack>
      {loading ? (
        <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { padding: '40px 0' } }}>
          <Loader type={LoaderType.spinner} />
        </Stack>
      ) : (
        <Stack>
          {!readOnly && (
            <MemberListItem
              updateRoleBindings={handleUpdateRoleBindings}
              usersInWorkspaceRolesList={usersInWorkspaceRolesList}
              readOnly={readOnly}
            />
          )}

          {usersWithRoles.length === 0 && !loading ? (
            <Stack styles={{ root: { padding: '12px 4px' } }}>No members to show</Stack>
          ) : (
            <>
              {usersWithRoles.length > 0 && (
                <Separator styles={{ root: { margin: '16px 0' } }}>Assigned roles</Separator>
              )}

              {usersWithRoles.map((user) => {
                const userRoleBindings = getUserRoleBindings(user.name);
                const isOwner = userRoleBindings.some(({ role }) => isOwnerRole(role));

                return (
                  <MemberListItem
                    key={user.name}
                    disableRemoveButton={!hasMoreThanOneOwner && isOwner}
                    prepopulatedRoleBindings={userRoleBindings}
                    updateRoleBindings={handleUpdateRoleBindings}
                    usersInWorkspaceRolesList={usersInWorkspaceRolesList}
                    readOnly={readOnly}
                  />
                );
              })}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
